import { useEffect, useMemo, useState } from 'react';

import { isAccessDeniedError } from '../../api/errors.js';
import { useDataHubClient } from '../../context/DataHubClientContext.js';
import { DataHubService } from '../../services/dataHubService.js';

import type { UseDatalakeAccessResult } from './useDatalakeAccess.interface.js';

/**
 * Probes Data Hub with the cheapest authoritative read so a page can gate its
 * write actions up front, rather than letting a user invest work that the
 * final save would reject.
 */
export function useDatalakeAccess(): UseDatalakeAccessResult {
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);

  const [isChecking, setIsChecking] = useState(true);
  const [isDenied, setIsDenied] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await service.searchDatasets({ pageSize: 1, includeCount: false });
        if (!cancelled) setIsDenied(false);
      } catch (err: unknown) {
        // Only an explicit authorization failure gates the UI — a transient
        // outage must not block a user who does hold the entitlement.
        if (!cancelled) setIsDenied(isAccessDeniedError(err));
      } finally {
        if (!cancelled) setIsChecking(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [service]);

  return { isChecking, isDenied };
}
